import { useEffect, useState, useCallback, useRef } from 'react'
import { find as SVGFind, List, Element } from '@svgdotjs/svg.js'
import { motion, AnimatePresence } from 'framer-motion'
import { importStatus } from './types'

const rectData = [
  { x: 235, y: 178, fill: '#CAD8DE' },
  { x: 109, y: 45, fill: '#CAD8DE' },
  { x: 277, y: 133, fill: '#A9C5D0' },
  { x: 326, y: 181, fill: '#A9C5D0' },
  { x: 59, y: 69, fill: '#A9C5D0' },
  { x: 262, y: 37, fill: '#DBE9F0' },
  { x: 59, y: 162, fill: '#DBE9F0' },
  { x: 0, y: 117, fill: '#DBE9F0' },
  { x: 299, y: 82, fill: '#DBE9F0' },
  { x: 109, y: 186, fill: '#DBE9F0' },
  { x: 183, y: 202, fill: '#DBE9F0' },
  { x: 178, y: 0, fill: '#A9C5D0' },
]

function calculateScale({ progress, startScale }) {
  const endScale = 1
  const currentScale = startScale + (endScale - startScale) * progress
  return currentScale
}

const ProgressCircle = ({ progress = 0, status }) => {
  const [hasCompleted, setHasCompleted] = useState(progress === 1)
  const progressComplete = progress === 1
  const scale = calculateScale({ progress, startScale: 0.71 })
  const isUploadingStatus = status === importStatus.UPLOADING
  const isUploadedAndInProgress = status === importStatus.IN_PROGRESS
  const readyToPlayCompletedAnimation = !hasCompleted && progressComplete
  const isFailedStatus = status === importStatus.FAILED
  const isDefaultStatus = status === importStatus.DEFAULT

  // We only want the animation to play once when progress reaches 1.
  // We need to utilize a combination of state switches to determine when to play the animation,
  // since opening the popover will cause the component to re-render.
  useEffect(() => {
    if (progress < 1) return
    if (hasCompleted) return

    // Set hasCompleted to true when progress reaches 1 and has not been completed yet.
    setHasCompleted(true)
  }, [progress, hasCompleted])

  // Reset completion state when status changes to default.
  useEffect(() => {
    if (!hasCompleted) return
    if (!isDefaultStatus) return

    setHasCompleted(false)
  }, [isDefaultStatus, hasCompleted])

  const filledCircleColor = isFailedStatus
    ? '#B0160B'
    : progressComplete || isUploadedAndInProgress
      ? '#5DD39E'
      : '#30505A'

  return (
    <AnimatePresence key="progress-circle-framer">
      <motion.circle
        key="progress-circle"
        className="progress-circle"
        cx="50%"
        cy="50%"
        r="72"
        fill="#5DD39E"
        initial={{ scale: 0.69 }}
        animate={{ scale: !isUploadingStatus ? 0.69 : scale }}
        transition={{
          duration: 0.5,
          delay: 0,
          ease: 'easeInOut',
        }}
        exit={{ opacity: 0, transition: { duration: 1 } }}
      />
      <motion.circle
        key="background-circle"
        cx="50%"
        cy="50%"
        r="50.5"
        fill="#30505A"
        animate={{ fill: filledCircleColor }}
        transition={{
          fill: { duration: !readyToPlayCompletedAnimation ? 0 : 1 },
        }}
      />
      {isFailedStatus ? (
        <svg
          key="failed-icon"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          width="100"
          height="100"
          x="86"
          y="39"
          viewBox="0 0 100 100"
        >
          <motion.path
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            d="M67.4167 77.75H72.5834V82.9167H67.4167V77.75ZM67.4167 57.0833H72.5834V72.5833H67.4167V57.0833ZM69.9742 44.1667C55.7142 44.1667 44.1667 55.74 44.1667 70C44.1667 84.26 55.7142 95.8333 69.9742 95.8333C84.26 95.8333 95.8334 84.26 95.8334 70C95.8334 55.74 84.26 44.1667 69.9742 44.1667ZM70 90.6667C58.5817 90.6667 49.3334 81.4183 49.3334 70C49.3334 58.5817 58.5817 49.3333 70 49.3333C81.4184 49.3333 90.6667 58.5817 90.6667 70C90.6667 81.4183 81.4184 90.6667 70 90.6667Z"
            fill="#E7BFBD"
          />
        </svg>
      ) : progressComplete || isUploadedAndInProgress ? (
        <svg
          key="checkmark"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          width="64"
          height="64"
          x="123"
          y="78"
          viewBox="0 0 24 24"
          strokeWidth={1.8}
          stroke="#2B6149"
        >
          <motion.path
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={
              !readyToPlayCompletedAnimation
                ? { pathLength: 1, opacity: 1 }
                : { pathLength: 0, opacity: 0 }
            }
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            d="M4.5 12.75l6 6 9-13.5"
          />
        </svg>
      ) : (
        <path
          style={{ transform: 'translate(-8%, 0%)' }}
          d="M159.333 111L162.976 114.643L177.417 100.228V131.667H182.583V100.228L196.998 114.668L200.667 111L180 90.3333L159.333 111Z"
          fill="#EFF8FD"
        />
      )}
    </AnimatePresence>
  )
}

export const UploaderAnimation = ({
  children,
  status,
  uploadInProgress = false,
  uploadComplete = false,
}) => {
  const [svgs, setSvgs] = useState<{
    rects: List<Element> | null
    centerCircle: List<Element> | null
    root: SVGElement | null
  }>({
    rects: null,
    centerCircle: null,
    root: null,
  })
  const animationStartedRef = useRef(false)

  const isFailedStatus = status === importStatus.FAILED
  const isDefaultStatus = status === importStatus.DEFAULT
  const isUploadedAndInProgress = status === importStatus.IN_PROGRESS
  const getRandomDelay = () => Math.random() * 4000
  const getRandomDuration = () => 2000 + Math.random() * 2000
  const centerCircleNode = svgs.centerCircle?.[0]

  const handleSvgRef = useCallback(node => {
    const rects = SVGFind('.cells')
    const centerCircle = SVGFind('.center-circle')
    setSvgs({
      rects,
      centerCircle,
      root: node,
    })
  }, [])

  // Reset animationStartedRef when status changes to default.
  // Allows the animation scene to reset.
  if (isDefaultStatus && animationStartedRef.current) {
    animationStartedRef.current = false

    svgs.rects?.each(element => {
      element.opacity(1)
    })
  }

  if (svgs.root && uploadInProgress && !uploadComplete && !animationStartedRef.current) {
    animationStartedRef.current = true
    const circle: SVGGraphicsElement = svgs.root.querySelector('.center-circle')!
    const { x: circleOrigX, y: circleOrigY, width, height } = circle.getBBox()
    const circleX = circleOrigX + width / 2
    const circleY = circleOrigY + height / 2

    svgs.rects?.each(element => {
      const delay = getRandomDelay()
      const duration = getRandomDuration()

      element
        .animate({
          duration,
          delay,
          when: 'now',
          swing: false,
          times: Infinity,
          wait: 200,
        })
        .move(circleX, circleY)
    })

    centerCircleNode?.animate(1000, 0).attr({ r: 85 }).loop(Infinity, true)
  }

  // Stop animations when upload is complete.
  if (uploadComplete || isFailedStatus || isUploadedAndInProgress) {
    svgs.rects?.each(element => {
      element.opacity(0).timeline().stop()
    })

    centerCircleNode?.animate(1000).attr({ r: 70 }).timeline().stop()
  }

  return (
    <svg
      ref={handleSvgRef}
      width="310"
      height="218"
      viewBox="0 0 310 218"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <clipPath id="cut-off-bottom">
          <rect x="0" y="0" width="310" height="218" />
        </clipPath>
      </defs>
      {rectData.map(rect => (
        <rect
          key={`anim-cell-${rect.x}-${rect.y}`}
          className="cells"
          x={rect.x}
          y={rect.y}
          width="55"
          height="16"
          rx="4"
          fill={rect.fill}
        />
      ))}

      <circle className="center-circle" cx="50%" cy="50%" r="70" fill="#EFF8FD" />
      {children}
    </svg>
  )
}

UploaderAnimation.ProgressCircle = ProgressCircle
